/**
 * Toast notification system — replaces all alert() calls.
 * Usage:
 *   import { toast } from "./Toast";
 *   toast.success("Posted!"), toast.error("Failed"), toast.info("Note"), toast.warn("Warning")
 */
import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

let _addToast = null;

const ICONS = {
  success: (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warn: (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  ),
  info: (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const STYLES = {
  success: { border: "rgba(34,197,94,0.35)",  bg: "rgba(34,197,94,0.1)",  color: "#4ade80", glow: "0 0 20px rgba(34,197,94,0.2)" },
  error:   { border: "rgba(239,68,68,0.35)",   bg: "rgba(239,68,68,0.1)",  color: "#f87171", glow: "0 0 20px rgba(239,68,68,0.2)" },
  warn:    { border: "rgba(245,158,11,0.35)",  bg: "rgba(245,158,11,0.1)", color: "#fbbf24", glow: "0 0 20px rgba(245,158,11,0.2)" },
  info:    { border: "rgba(6,182,212,0.35)",   bg: "rgba(6,182,212,0.1)",  color: "#22d3ee", glow: "0 0 20px rgba(6,182,212,0.2)" },
};

function ToastItem({ id, type, message, onRemove }) {
  const [visible, setVisible] = useState(false);
  const s = STYLES[type] || STYLES.info;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const t = setTimeout(() => { setVisible(false); setTimeout(() => onRemove(id), 300); }, 4200);
    return () => clearTimeout(t);
  }, [id, onRemove]);

  return (
    <div
      onClick={() => { setVisible(false); setTimeout(() => onRemove(id), 300); }}
      style={{
        background: "rgba(10,10,14,0.95)",
        border: `1px solid ${s.border}`,
        boxShadow: s.glow + ", 0 8px 32px rgba(0,0,0,0.5)",
        backdropFilter: "blur(20px)",
        transform: visible ? "translateX(0) scale(1)" : "translateX(100%) scale(0.9)",
        opacity: visible ? 1 : 0,
        transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)",
        maxWidth: "360px",
        minWidth: "240px",
      }}
      className="flex items-start gap-3 px-4 py-3 rounded-2xl cursor-pointer select-none"
    >
      <span style={{ color: s.color, marginTop: "2px" }}>{ICONS[type]}</span>
      <span className="text-sm text-gray-200 leading-snug flex-1">{message}</span>
      <span style={{ color: s.color }} className="text-xs opacity-60 mt-0.5">✕</span>
    </div>
  );
}

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev.slice(-4), { id, type, message }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => { _addToast = addToast; return () => { _addToast = null; }; }, [addToast]);

  if (!toasts.length) return null;

  return createPortal(
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none">
      {toasts.map(t => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem {...t} onRemove={removeToast} />
        </div>
      ))}
    </div>,
    document.body
  );
}

// ── Public API ────────────────────────────────────────────────────────────────
export const toast = {
  success: (msg) => _addToast?.("success", msg),
  error:   (msg) => _addToast?.("error",   msg),
  warn:    (msg) => _addToast?.("warn",    msg),
  info:    (msg) => _addToast?.("info",    msg),
};

export default toast;
